import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCheck, Maximize2, Loader2, Clock, CheckCircle } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function BillingConfirmationsWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchPendingBilling();
  }, []);

  const fetchPendingBilling = async () => {
    try {
      const result = await apiRequest('/warehouse/pending-billing-confirmations');
      setData(result);
    } catch (error) {
      console.error('Failed to load billing confirmations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 h-full">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const pending = data?.pending || [];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 h-full" data-testid="billing-confirmations-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-cyan-600" />
          <h3 className="font-semibold text-slate-900">Billing Confirmations</h3>
          {pending.length > 0 && (
            <Badge className="bg-amber-100 text-amber-700 text-xs">{pending.length}</Badge>
          )}
        </div>
        {expandable && expandPath && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(expandPath)}
            className="h-8 w-8 p-0"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {pending.length > 0 ? (
        <div className="space-y-2">
          {pending.slice(0, 4).map(item => (
            <div 
              key={item.id} 
              className="p-2 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100"
              onClick={() => navigate('/warehouse/billing-confirmations')}
            >
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-slate-900">{item.store_name}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {new Date(item.delivery_datetime).toLocaleDateString()} • {item.submitted_by}
              </div>
            </div>
          ))}
          
          {pending.length > 4 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full"
              onClick={() => navigate('/warehouse/billing-confirmations')}
            >
              View all {pending.length} pending
            </Button>
          )}
        </div>
      ) : (
        <div className="text-center py-4 text-slate-500 text-sm">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
          <p>All billing confirmed</p>
        </div>
      )}
    </div>
  );
}
